import React, { useState, useEffect } from 'react';
import { FaStar } from 'react-icons/fa';
import { getTeacherLatestFeedback } from '../bookingService';

/**
 * Recent reviews component showing latest student feedback
 * Displays star rating, comment, tags, and date for each review
 */
export default function RecentReviews({ teacherEmail, isArabic = false, limit = 5 }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchReviews() {
      if (!teacherEmail) return;

      setLoading(true);
      const { data, error } = await getTeacherLatestFeedback(teacherEmail, limit);

      if (!error && data) {
        setReviews(data);
      }
      setLoading(false);
    }

    fetchReviews();
  }, [teacherEmail, limit]);

  if (loading) {
    return (
      <div style={{
        padding: '16px',
        color: '#94A3B8',
        fontSize: 13,
        fontFamily: "'IBM Plex Sans Arabic', sans-serif"
      }}>
        {isArabic ? 'جاري التحميل...' : 'Loading reviews...'}
      </div>
    );
  }

  if (!reviews || reviews.length === 0) {
    return (
      <div style={{
        padding: '16px',
        color: '#94A3B8',
        fontSize: 13,
        textAlign: 'center',
        fontFamily: "'IBM Plex Sans Arabic', sans-serif"
      }}>
        {isArabic ? 'لا توجد تقييمات بعد' : 'No reviews yet'}
      </div>
    );
  }

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return isArabic ? 'اليوم' : 'Today';
    } else if (diffDays === 1) {
      return isArabic ? 'أمس' : 'Yesterday';
    } else if (diffDays < 7) {
      return isArabic ? `منذ ${diffDays} أيام` : `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return isArabic
        ? `منذ ${weeks} ${weeks === 1 ? 'أسبوع' : 'أسابيع'}`
        : `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    } else {
      const months = Math.floor(diffDays / 30);
      return isArabic
        ? `منذ ${months} ${months === 1 ? 'شهر' : 'أشهر'}`
        : `${months} ${months === 1 ? 'month' : 'months'} ago`;
    }
  };

  // Render stars for a rating
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={i} color="#FFB400" size={12} />);
    }

    // Add empty stars to fill up to 5
    for (let i = fullStars; i < 5; i++) {
      stars.push(<FaStar key={i} color="#E5E7EB" size={12} />);
    }

    return stars;
  };

  return (
    <div style={{
      padding: '16px 0'
    }}>
      {/* Header */}
      <div style={{
        fontSize: 13,
        color: '#64748B',
        marginBottom: 12,
        fontWeight: 600,
        fontFamily: "'IBM Plex Sans Arabic', sans-serif"
      }}>
        {isArabic ? 'أحدث التقييمات' : 'Recent Reviews'}
      </div>

      {/* Reviews list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {reviews.map((review, index) => (
          <div
            key={index}
            style={{
              padding: 12,
              background: '#F8FAFC',
              borderRadius: 12,
              border: '1px solid #F1F5F9'
            }}
          >
            {/* Rating and date */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8
            }}>
              {/* Stars */}
              <div style={{ display: 'flex', gap: 2 }}>
                {renderStars(review.rating)}
              </div>

              {/* Date */}
              <div style={{
                fontSize: 11,
                color: '#94A3B8',
                fontFamily: "'IBM Plex Sans Arabic', sans-serif"
              }}>
                {formatDate(review.created_at)}
              </div>
            </div>

            {/* Comment */}
            {review.comment && (
              <div style={{
                fontSize: 13,
                color: '#475569',
                lineHeight: 1.5,
                marginBottom: review.tags && review.tags.length > 0 ? 8 : 0,
                fontFamily: "'IBM Plex Sans Arabic', sans-serif"
              }}>
                {review.comment}
              </div>
            )}

            {/* Tags */}
            {review.tags && review.tags.length > 0 && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6
              }}>
                {review.tags.map((tag, tagIndex) => (
                  <span
                    key={tagIndex}
                    style={{
                      fontSize: 11,
                      padding: '4px 8px',
                      background: '#E0EAFF',
                      color: '#1e40af',
                      borderRadius: 6,
                      fontWeight: 500,
                      fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* See more hint (if there could be more reviews) */}
      {reviews.length >= limit && (
        <div style={{
          marginTop: 12,
          fontSize: 12,
          color: '#94A3B8',
          textAlign: 'center',
          fontFamily: "'IBM Plex Sans Arabic', sans-serif"
        }}>
          {isArabic ? `عرض أحدث ${limit} تقييمات` : `Showing latest ${limit} reviews`}
        </div>
      )}
    </div>
  );
}
